import { useState, useEffect } from "react";
import Table from "../../../components/table";
import Input from "../../../components/input";
import { capitalizeFirstLetter } from "../../../utilz/function";
import ActionModal from "./action-modal";
import { useParams } from "react-router-dom";
import useRequest from "../../../components/hooks/use-request";
import { showToast } from "../../../components/toast";

interface RegionDetailsData {
    _id: string;
    regionName: string;
    phone: number;
    sourcingOfficers: any;
    email: string;
    sourcingState: any;
}

interface RegionDetailsProps {
    data: RegionDetailsData[];
}

const RegionDetails: React.FC<RegionDetailsProps> = ({ data }) => {
    const [filteredData, setFilteredData] = useState<RegionDetailsData[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const { id } = useParams<{ id: string }>();

    const [modalConfig, setModalConfig] = useState<{
        isVisible: boolean;
        actionType: "remove";
        selectedRegionId: string | null;
    }>({
        isVisible: false,
        actionType: "remove",
        selectedRegionId: null,
    });

    const columns = [
        { header: "Sourcing Officer Name", accessor: "username" },
        { header: "Email Address", accessor: "email" },
        { header: "Phone Number", accessor: "phone" },
        { header: "Sourcing State", accessor: "sourcingState" },
        { header: "Actions", accessor: "_id" },
    ];

    const { makeRequest: deleteRegionOfficer } = useRequest(
        `/region/${id}/officers/${modalConfig.selectedRegionId}`,
        "DELETE"
    );

    useEffect(() => {
        const filtered = data?.filter((regions) => {
            const searchMatch =
                !searchQuery ||
                regions?.sourcingOfficers
                    ?.toLowerCase()
                    .includes(searchQuery?.toLowerCase());

            return searchMatch;
        });
        setFilteredData(filtered);
    }, [data, searchQuery]);

    const combinedData = filteredData?.map((regions) => ({
        ...regions,

        name: (
            <div>
                <div>{capitalizeFirstLetter(regions?.regionName) || "N/A"}</div>
            </div>
        ),
    }));

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleRemove = (regionId: string) => {
        // Set the modal state to visible and set the action type
        setModalConfig({
            isVisible: true,
            actionType: "remove",
            selectedRegionId: regionId,
        });
    };

    const handleConfirmAction = async () => {
       const [response] = await deleteRegionOfficer()
       if (response?.status) {
            showToast(response.message, true);
            window.location.reload();
            } else {
            showToast(response?.message, false);
        }
    };

    return (
        <div className="py-3 bg-white rounded-3xl shadow border border-[#C9C9C9] mt-10 pt-5">
            <div className="md:flex justify-between px-5">
                <p className="text-[20px] font-bold">Sourcing Regions</p>

                <div className="md:flex gap-2 items-center md:mt-0 mt-2">
                    <Input
                        name="search"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="w-full"
                        placeholder="Search"
                        icon="searchIcon"
                    />
                </div>
            </div>

            {combinedData?.length > 0 ? (
                <Table
                    columns={columns}
                    data={combinedData}
                    selectedUserId={null}
                    onRemoveClick={handleRemove}
                />
            ) : (
                <div className="opacity-80 mt-10 font-bold text-center mx-auto">
                    No Region officer found
                </div>
            )}

            <ActionModal
                visible={modalConfig.isVisible}
                onCancel={() =>
                    setModalConfig({ ...modalConfig, isVisible: false })
                }
                onConfirm={handleConfirmAction}
                title={
                    modalConfig.actionType === "remove"
                        ? "Remove Sourcing Officer"
                        : "nill"
                }
                content={`Are you sure you want to ${modalConfig.actionType} this Sourcing officer?`}
                actionType={modalConfig.actionType}
            />
        </div>
    );
};

export default RegionDetails;
